<script setup lang="ts">
import CommonBtn from '@/components/common-btn/CommonBtn.vue'
import FooterBar from '@/components/footer-bar/FooterBar.vue'
import { ref, watch, computed } from 'vue'
import { useUserStore } from '@/stores/modules/user'
import JoinGroup from './components/JoinGroup.vue'
import { useCommonStore } from '@/stores/modules/common'
import { formatNumberWithCommas, formatNumberWithWan } from '@/utils'
import Notice from './components/Notice.vue'
import { ElNotification, dayjs } from 'element-plus'
import {
  Coin,
  Close,
  Connection,
  DataBoard,
  Edit,
  MessageBox,
  Present,
  Monitor,
  Odometer,
  Rank,
  Setting,
  Operation,
  ShoppingBag,
  Wallet,
  Collection,
  Star,
  Grape,
  MagicStick,
  ColdDrink,
  Brush,
  Reading,
  Files,
  TakeawayBox,
  Sunny,
} from '@element-plus/icons-vue'
import NoviceCheckIn from './components/NoviceCheckIn.vue'
import { getImportantNoticeApi } from '@/api/important-notice/'
import { socketEmitAsync, socketOn } from '@/utils/web-socket'
import ActivityCheckIn from './components/ActivityCheckIn.vue'
import TestServerCdkey from './components/TestServerCdkey.vue'
import { VueDraggable } from 'vue-draggable-plus'
import { cloneDeep } from 'lodash'
import { homeModuleMetaList, normalizeHomeModuleSetting, type HomeModuleKey, type HomeModuleSettingType } from '@/types/home-module'

const envMode = import.meta.env.VITE_ENV_MODE

const commonStore = useCommonStore()
const userStore = useUserStore()

const joinGroupShow = ref(false)
const noticeShow = ref(false)
const noviceCheckInShow = ref(false)
const activityShow = ref(false)

const importantNotice = ref('')
async function getImportantNotice() {
  const res = await getImportantNoticeApi(1)
  if (res) importantNotice.value = res
  if (envMode === 'test') importantNotice.value = '请勿对外爆料任何测试服新内容！'
}
function getPetOutsideReward() {
  const resetDiffDay = dayjs().diff(dayjs(userStore.outsidePetResetTime || '1970-01-01').format('YYYY-MM-DD 00:00:00'), 'day')
  if (resetDiffDay < 1 || !userStore.petList?.length) return
  socketEmitAsync<number>({ type: 'getPetOutsideReward' }).then((res) => {
    if (res) {
      userStore.gold += res
      userStore.outsidePetResetTime = dayjs().format('YYYY-MM-DD')
      ElNotification({
        title: '魔物外出归来',
        message: `魔物为您带来了${formatNumberWithCommas(res)} 金币，已自动放入背包`,
        position: 'top-left',
        duration: 10000,
      })
    }
  })
}

const isVendorRefresh = computed(() => dayjs().diff(dayjs(userStore.vendorResetTime || '1970-01-01').format('YYYY-MM-DD 00:00:00'), 'day') >= 1)
const isFreeBless = computed(
  () => dayjs().diff(dayjs(userStore.runeBlessResetTime || '1970-01-01').format('YYYY-MM-DD 00:00:00'), 'day') >= 1 && userStore.soulType,
)

const testServerCdkeyShow = ref(false)
const isCustomizeMode = ref(false)
const customizeBackup = ref<HomeModuleSettingType | null>(null)
const homeModuleSetting = ref<HomeModuleSettingType>(normalizeHomeModuleSetting(commonStore.homeModuleSetting))
const draggableModuleKeyList = ref<HomeModuleKey[]>([])

const shouldRuntimeShowMap = computed<Record<HomeModuleKey, boolean>>(() => ({
  joinGroup: true,
  notice: true,
  userSetting: true,
  mall: true,
  blackMarket: true,
  commerce: true,
  warehouse: true,
  smithy: true,
  institute: true,
  equipHandbook: true,
  treasury: true,
  pub: true,
  occultScience: true,
  darkGoldWorkshop: true,
  rank: true,
  supremeWorkshop: userStore.advanceLevel === 4 && (envMode === 'test' || envMode === 'dev'),
  magicWorkshop: userStore.advanceLevel === 4 && (envMode === 'test' || envMode === 'dev'),
  noviceCheckIn: userStore.noviceCheckInIndex < 7,
  petRanch: true,
  awaken: true,
  secondAwaken: userStore.advanceLevel === 4 && (envMode === 'test' || envMode === 'dev'),
  relic: true,
  activityCheckIn: dayjs().isBefore('2026-01-31 00:00:00'),
  activityCelebration: dayjs().isBefore('2026-03-08 00:00:00'),
  testServerCdkeyDraw: dayjs().isBefore('2026-04-12 00:00:00') && envMode === 'prod',
}))

const visibleModuleKeyList = computed(() => {
  return homeModuleSetting.value.order.filter((key) => homeModuleSetting.value.config[key].show && shouldRuntimeShowMap.value[key])
})
const removedModuleKeyList = computed(() => {
  return homeModuleSetting.value.order.filter((key) => !homeModuleSetting.value.config[key].show && shouldRuntimeShowMap.value[key])
})
const homeModuleLabelMap = Object.fromEntries(homeModuleMetaList.map((item) => [item.key, item.label])) as Record<HomeModuleKey, string>

const moduleStyle = (key: HomeModuleKey) => ({
  gridColumn: `span ${homeModuleSetting.value.config[key].span}`,
})

watch(
  () => [visibleModuleKeyList.value.join(','), isCustomizeMode.value],
  () => {
    draggableModuleKeyList.value = [...visibleModuleKeyList.value]
  },
  { immediate: true },
)

function handleDone(cb: () => void) {
  if (isCustomizeMode.value) return
  cb()
}

function handleModuleRemove(key: HomeModuleKey) {
  homeModuleSetting.value.config[key].show = false
}

function handleModuleRestore(key: HomeModuleKey) {
  homeModuleSetting.value.config[key].show = true
}

function handleModuleSpanEdit(key: HomeModuleKey) {
  const oldSpan = homeModuleSetting.value.config[key].span
  homeModuleSetting.value.config[key].span = oldSpan === 3 ? 1 : ((oldSpan + 1) as 1 | 2 | 3)
}

function handleModuleDragEnd() {
  const dragOrder = [...draggableModuleKeyList.value]
  const otherKeyList = homeModuleSetting.value.order.filter((key) => !dragOrder.includes(key))
  homeModuleSetting.value.order = [...dragOrder, ...otherKeyList]
}

function openCustomizeMode() {
  customizeBackup.value = cloneDeep(homeModuleSetting.value)
  isCustomizeMode.value = true
}

function cancelCustomizeMode() {
  if (customizeBackup.value) {
    homeModuleSetting.value = cloneDeep(customizeBackup.value)
  }
  isCustomizeMode.value = false
}

function resetCustomizeModeToDefault() {
  homeModuleSetting.value = normalizeHomeModuleSetting()
}

async function getHomeModuleSetting() {
  const res = await socketEmitAsync<HomeModuleSettingType>({ type: 'getHomeModuleSetting', hideLoading: true })
  homeModuleSetting.value = normalizeHomeModuleSetting(res)
  commonStore.homeModuleSetting = homeModuleSetting.value
  commonStore.homeModuleSettingLoaded = true
}

async function saveHomeModuleSetting() {
  const res = await socketEmitAsync<HomeModuleSettingType>({
    type: 'setHomeModuleConfig',
    data: {
      config: homeModuleSetting.value.config,
      order: homeModuleSetting.value.order,
    },
  })
  homeModuleSetting.value = normalizeHomeModuleSetting(res)
  commonStore.homeModuleSetting = homeModuleSetting.value
  commonStore.homeModuleSettingLoaded = true
  isCustomizeMode.value = false
}

watch(
  () => commonStore.homeModuleSetting,
  (val) => {
    homeModuleSetting.value = normalizeHomeModuleSetting(val)
  },
  { deep: true },
)

watch(
  () => commonStore.isSocketInit,
  (val) => {
    if (!val) return
    getImportantNotice()
    getPetOutsideReward()
    if (!commonStore.homeModuleSettingLoaded) {
      getHomeModuleSetting()
    }

    socketOn({
      type: 'getNoticeList',
      callBack: getImportantNotice,
    })
  },
)
</script>

<template>
  <div class="home-page page" v-bind="$attrs">
    <div class="top-bar" @click="commonStore.path = 'User'">
      <span>
        ✨<span class="nickname" style="font-weight: bold" :style="{ background: userStore.nicknameColor }">{{ userStore.nickname }}</span>
      </span>
      <div style="font-size: 12px">
        <span>💰 {{ formatNumberWithWan(userStore.gold) }}</span>
        <span style="margin-left: 12px">💎 {{ formatNumberWithWan(userStore.diamond) }}</span>
        <span style="margin-left: 12px" v-if="userStore.crystal || userStore.vipLevel">🔮 {{ formatNumberWithWan(userStore.crystal) }}</span>
      </div>
    </div>
    <el-alert
      :title="`通知：${importantNotice}`"
      type="warning"
      show-icon
      :closable="false"
      :style="{ visibility: importantNotice ? '' : 'hidden', margin: importantNotice ? '10px 0' : '5px 0' }"
    />
    <div class="home-btn-wrap">
      <div class="customize-bar" style="grid-column: span 3" v-if="userStore.advanceLevel === 4">
        <el-button v-if="!isCustomizeMode" plain size="small" :icon="Operation" @click="openCustomizeMode">自定义</el-button>
        <template v-else>
          <el-button plain type="success" size="small" @click="resetCustomizeModeToDefault">恢复默认</el-button>
          <el-button plain type="primary" size="small" @click="saveHomeModuleSetting">保存布局</el-button>
          <el-button plain type="danger" size="small" @click="cancelCustomizeMode">取消</el-button>
        </template>
      </div>

      <VueDraggable
        v-model="draggableModuleKeyList"
        :animation="150"
        handle=".module-tool-drag"
        @end="handleModuleDragEnd"
        class="home-module-dragger"
        style="grid-column: span 3"
      >
        <div v-for="key in draggableModuleKeyList" :key="key" class="module-item" :style="moduleStyle(key)">
          <div class="module-tools" v-if="isCustomizeMode">
            <el-icon class="module-tool-btn module-tool-drag drag"><Rank /></el-icon>
            <div>
              <el-icon class="module-tool-btn edit" @click.stop="handleModuleSpanEdit(key)"><Edit /></el-icon>
              <el-icon class="module-tool-btn del" @click.stop="handleModuleRemove(key)" style="margin-left: 3px"><Close /></el-icon>
            </div>
          </div>

          <template v-if="key === 'joinGroup'">
            <CommonBtn type="primary" @done="handleDone(() => (joinGroupShow = true))"
              ><el-icon><Connection /></el-icon><span>加入官方群</span></CommonBtn
            >
          </template>
          <template v-else-if="key === 'notice'">
            <CommonBtn type="success" @done="handleDone(() => (noticeShow = true))"
              ><el-icon><DataBoard /></el-icon><span>公告栏</span></CommonBtn
            >
          </template>
          <template v-else-if="key === 'userSetting'">
            <CommonBtn type="primary" @done="handleDone(() => (commonStore.path = 'User'))"
              ><el-icon><Setting /></el-icon><span>设置</span></CommonBtn
            >
          </template>
          <template v-else-if="key === 'mall'">
            <CommonBtn type="warning" @done="handleDone(() => (commonStore.path = 'Mall'))"
              ><el-icon><ShoppingBag /></el-icon><span>商城</span></CommonBtn
            >
          </template>
          <template v-else-if="key === 'blackMarket'">
            <el-badge :value="isVendorRefresh ? '新' : ''" :hidden="!isVendorRefresh" :offset="[-15, 0]" style="width: 100%">
              <CommonBtn
                type="success"
                @done="handleDone(() => (commonStore.path = 'BlackMarket'))"
                :isLock="userStore.dailyChallengeLevel.diamond < 2"
                lockMsg="通过钻石挑战 2级开启黑市"
              >
                <el-icon><Wallet /></el-icon>
                <span>黑市</span>
              </CommonBtn>
            </el-badge>
          </template>
          <template v-else-if="key === 'commerce'">
            <CommonBtn
              type="success"
              @done="handleDone(() => (commonStore.path = 'Commerce'))"
              :isLock="userStore.dailyChallengeLevel.gold < 5"
              lockMsg="通过金币挑战 5级开启商会"
            >
              <el-icon><Coin /></el-icon>
              <span>商会</span>
            </CommonBtn>
          </template>
          <template v-else-if="key === 'warehouse'">
            <CommonBtn type="success" @done="handleDone(() => (commonStore.path = 'Warehouse'))"
              ><el-icon><MessageBox /></el-icon><span>仓库</span></CommonBtn
            >
          </template>
          <template v-else-if="key === 'smithy'">
            <CommonBtn type="success" @done="handleDone(() => (commonStore.path = 'Smithy'))"
              ><el-icon><Odometer /></el-icon><span>铁匠铺</span></CommonBtn
            >
          </template>
          <template v-else-if="key === 'institute'">
            <CommonBtn type="success" @done="handleDone(() => (commonStore.path = 'Institute'))"
              ><el-icon><Monitor /></el-icon><span>研究所</span></CommonBtn
            >
          </template>
          <template v-else-if="key === 'equipHandbook'">
            <CommonBtn type="success" @done="handleDone(() => (commonStore.path = 'EquipHandbook'))"
              ><el-icon><Collection /></el-icon><span>图鉴</span></CommonBtn
            >
          </template>
          <template v-else-if="key === 'treasury'">
            <CommonBtn type="primary" @done="handleDone(() => (commonStore.path = 'Treasury'))"
              ><el-icon><Files /></el-icon><span>持有道具</span></CommonBtn
            >
          </template>
          <template v-else-if="key === 'pub'">
            <CommonBtn color="#b44c97" :dark="commonStore.isDarkMode" @done="handleDone(() => (commonStore.path = 'Pub'))"
              ><el-icon><ColdDrink /></el-icon><span>酒馆</span></CommonBtn
            >
          </template>
          <template v-else-if="key === 'occultScience'">
            <CommonBtn
              type="success"
              @done="handleDone(() => (commonStore.path = 'OccultScience'))"
              :isLock="!userStore.soulType"
              lockMsg="觉醒后开启秘术坊"
            >
              <el-icon><MagicStick /></el-icon>
              <span>秘术坊</span>
            </CommonBtn>
          </template>
          <template v-else-if="key === 'darkGoldWorkshop'">
            <CommonBtn
              color="#f7c99c"
              :dark="commonStore.isDarkMode"
              @done="handleDone(() => (commonStore.path = 'DarkGoldWorkshop'))"
              :isLock="userStore.advanceLevel !== 4"
              lockMsg="进阶后开启暗金工坊"
            >
              <el-icon><TakeawayBox /></el-icon>
              <span>暗金工坊</span>
            </CommonBtn>
          </template>
          <template v-else-if="key === 'rank'">
            <CommonBtn type="primary" @done="handleDone(() => (commonStore.path = 'Rank'))"
              ><el-icon><Reading /></el-icon><span>奇闻</span></CommonBtn
            >
          </template>
          <template v-else-if="key === 'supremeWorkshop'">
            <CommonBtn
              color="#d4af37"
              :dark="commonStore.isDarkMode"
              @done="handleDone(() => (commonStore.path = 'SupremeWorkshop'))"
              :isLock="userStore.peakLevel !== 8"
              lockMsg="二觉后开启至臻工坊"
            >
              <el-icon><TakeawayBox /></el-icon>
              <span>至臻工坊</span>
            </CommonBtn>
          </template>
          <template v-else-if="key === 'magicWorkshop'">
            <CommonBtn
              color="#4bc1ff"
              :dark="commonStore.isDarkMode"
              @done="handleDone(() => (commonStore.path = 'MagicWorkshop'))"
              :isLock="userStore.peakLevel !== 8"
              lockMsg="二觉后开启魔法工坊"
            >
              <el-icon><MagicStick /></el-icon>
              <span>魔法工坊</span>
            </CommonBtn>
          </template>
          <template v-else-if="key === 'noviceCheckIn'">
            <CommonBtn type="danger" @done="handleDone(() => (noviceCheckInShow = true))"
              ><el-icon><Star /></el-icon><span>新手签到</span></CommonBtn
            >
          </template>
          <template v-else-if="key === 'petRanch'">
            <CommonBtn
              color="#626aef"
              @done="handleDone(() => (commonStore.path = 'PetRanch'))"
              :dark="commonStore.isDarkMode"
              :isLock="userStore.petList.length === 0 && userStore.dailyChallengeLevel.diamond < 3"
              lockMsg="拥有魔物或通过钻石挑战 3级后开启魔物牧场"
            >
              <el-icon><Grape /></el-icon>
              <span>魔物牧场</span>
            </CommonBtn>
          </template>
          <template v-else-if="key === 'awaken'">
            <el-badge :value="isFreeBless ? '免费祈福' : ''" :hidden="!isFreeBless" :offset="[-50, 0]" style="width: 100%">
              <CommonBtn
                color="#fcc800"
                :dark="commonStore.isDarkMode"
                @done="handleDone(() => (commonStore.path = 'Awaken'))"
                :isLock="userStore.eternalTowerLevel < 30 && !userStore.soulType"
                lock-msg="通过不朽之塔 30层开启觉醒殿堂"
              >
                <el-icon><Brush /></el-icon>
                <span>觉醒殿堂</span>
              </CommonBtn>
            </el-badge>
          </template>
          <template v-else-if="key === 'secondAwaken'">
            <CommonBtn
              color="#ff4500"
              :dark="commonStore.isDarkMode"
              @done="handleDone(() => (commonStore.path = 'SecondAwaken'))"
              :isLock="userStore.peakLevel !== 8"
              lock-msg="通过巅峰之路后开启二觉殿堂"
            >
              <el-icon><Brush /></el-icon>
              <span>二觉殿堂</span>
            </CommonBtn>
          </template>
          <template v-else-if="key === 'relic'">
            <CommonBtn
              color="#2792c3"
              :dark="commonStore.isDarkMode"
              @done="handleDone(() => (commonStore.path = 'Relic'))"
              :isLock="userStore.advanceLevel !== 4"
              lock-msg="进阶后开启圣物藏馆"
            >
              <el-icon><Sunny /></el-icon>
              <span>圣物藏馆</span>
            </CommonBtn>
          </template>
          <template v-else-if="key === 'activityCheckIn'">
            <el-badge value="限时" :offset="[-28, 0]" style="width: 100%">
              <CommonBtn color="#E63972" :dark="commonStore.isDarkMode" @done="handleDone(() => (activityShow = true))">
                <el-icon><Present /></el-icon>
                <span>元旦签到</span>
              </CommonBtn>
            </el-badge>
          </template>
          <template v-else-if="key === 'activityCelebration'">
            <el-badge value="签到" :hidden="!commonStore.canCheckIn" :offset="[-28, 0]" style="width: 100%">
              <CommonBtn color="#B22222 " type="danger" :dark="commonStore.isDarkMode" @done="handleDone(() => (commonStore.path = 'OneYear'))">
                <el-icon><Present /></el-icon>
                <span>新春庆典</span>
              </CommonBtn>
            </el-badge>
          </template>
          <template v-else-if="key === 'testServerCdkeyDraw'">
            <CommonBtn type="primary" :dark="commonStore.isDarkMode" @done="handleDone(() => (testServerCdkeyShow = true))">
              <el-icon><Present /></el-icon>
              <span>抽取测试服资格</span>
            </CommonBtn>
          </template>
        </div>
      </VueDraggable>

      <div class="removed-module-list" v-if="isCustomizeMode && removedModuleKeyList.length" style="grid-column: span 3">
        <h4>已移出模块（点击恢复）</h4>
        <div class="removed-module-tag-wrap">
          <el-tag
            v-for="key in removedModuleKeyList"
            :key="key"
            class="removed-module-tag"
            type="info"
            effect="plain"
            @click="handleModuleRestore(key)"
          >
            {{ homeModuleLabelMap[key] }}
          </el-tag>
        </div>
      </div>
    </div>

    <el-dialog v-model="joinGroupShow" width="100%" style="max-width: 500px">
      <JoinGroup />
      <template #footer>
        <p style="text-align: center">
          <el-button plain @click="joinGroupShow = false" size="small">关闭</el-button>
        </p>
      </template>
    </el-dialog>
    <el-dialog v-model="noticeShow" title="公告栏" width="100%" style="max-width: 500px">
      <Notice />
      <template #footer>
        <p style="text-align: center">
          <el-button plain @click="noticeShow = false" size="small">关闭</el-button>
        </p>
      </template>
    </el-dialog>

    <el-dialog v-model="noviceCheckInShow" title="新手签到" width="100%" style="max-width: 500px">
      <NoviceCheckIn @close="noviceCheckInShow = false" />
      <template #footer>
        <p style="text-align: center">
          <el-button plain @click="noviceCheckInShow = false" size="small">关闭</el-button>
        </p>
      </template>
    </el-dialog>
    <el-dialog v-model="activityShow" title="新春签到" width="100%" style="max-width: 500px" :destroy-on-close="false">
      <ActivityCheckIn @close="activityShow = false" />
      <template #footer>
        <p style="text-align: center">
          <el-button plain @click="activityShow = false" size="small">关闭</el-button>
        </p>
      </template>
    </el-dialog>
    <el-dialog v-model="testServerCdkeyShow" title="测试服资格抽取" width="100%" style="max-width: 500px">
      <TestServerCdkey @close="testServerCdkeyShow = false" />
      <template #footer>
        <p style="text-align: center">
          <el-button plain @click="testServerCdkeyShow = false" size="small">关闭</el-button>
        </p>
      </template>
    </el-dialog>
    <FooterBar />
  </div>
</template>

<style lang="scss" scoped>
.home-page {
  height: 100%;
  display: flex;
  flex-flow: column nowrap;
  overflow: hidden;
  .top-bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding-bottom: 5px;
    border-bottom: 1px var(--fontColor) solid;
  }
  .home-btn-wrap {
    width: 100%;
    overflow-y: auto;
    overflow-x: hidden;
    display: grid;
    flex-flow: row wrap;
    grid-template-columns: repeat(3, 1fr);
    gap: 10px;
    .customize-bar {
      display: flex;
      justify-content: flex-end;
      gap: 8px;
    }
    .home-module-dragger {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      column-gap: 10px;
      row-gap: 10px;
    }
    .module-item {
      position: relative;
      min-height: 46px;
      .module-tools {
        width: 100%;
        position: absolute;
        top: -8px;
        right: 0;
        z-index: 2;
        display: flex;
        justify-content: space-between;
        align-items: center;
        gap: 4px;
        .module-tool-btn {
          font-size: 20px;
          font-weight: bold;
          cursor: pointer;
          padding: 2px;
          border-radius: 50%;
          background: var(--el-fill-color-light);
          border: 1px solid var(--el-border-color-lighter);
          &.drag {
            background-color: var(--el-color-warning-light-3);
          }
          &.edit {
            background-color: var(--el-color-primary-light-3);
          }
          &.del {
            background-color: var(--el-color-danger-light-3);
          }
        }
        .module-tool-drag {
          cursor: grab;
        }
      }
    }
    .removed-module-list {
      border: 1px dashed var(--fontColor);
      padding: 8px;
      border-radius: 6px;
      h4 {
        margin: 0 0 8px;
        font-size: 13px;
      }
      .removed-module-tag-wrap {
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
      }
      .removed-module-tag {
        cursor: pointer;
      }
    }
    .common-btn-wrap {
      :deep() button {
        width: 100%;
        i {
          font-size: 20px;
        }
        span {
          margin-left: 0;
        }
      }
    }
  }
}
</style>
